import './styles.css';
import { store } from './state';
import { Model, kids, timeOf } from './model';
import { allExplanations, detect, rawFile } from './parsers';
import type { ParsedFile } from './parsers/types';
import { decode, decodePcm } from './audio/decode';
import { Peaks } from './audio/peaks';
import { Player } from './audio/player';
import { Anatomy, fitAll, playTarget, reveal, togglePlay, zoomTime, zoomToSelection } from './ui/anatomy';
import { Tree } from './ui/tree';
import { Inspector, download } from './ui/inspector';
import { bottomCards, compositionCard, encoderCard, summaryCard } from './ui/summary';
import { jsonReport, markdownReport } from './ui/report';
import { $, h, menu, toast } from './ui/dom';
import { fmtBytes } from './parsers/bits';
import { codecFor } from './codecs';
import { CodecCache } from './ui/codec';

const SAMPLES = [
  { id: 'mp3', label: 'MP3', file: 'sample.mp3', sub: 'LAME VBR · ID3 tags · cover art' },
  { id: 'wav', label: 'WAV', file: 'sample.wav', sub: '16-bit PCM · LIST/INFO tags' },
  { id: 'flac', label: 'FLAC', file: 'sample.flac', sub: 'Seek table · Vorbis comments · picture' },
  { id: 'm4a', label: 'M4A', file: 'sample.m4a', sub: 'AAC-LC · iTunes tags · cover art' },
  { id: 'ogg', label: 'Ogg Vorbis', file: 'sample.ogg', sub: 'Vorbis in Ogg pages' },
  { id: 'opus', label: 'Opus', file: 'sample.opus', sub: 'Opus in Ogg pages' },
];

const landing = $('#landing');
const app = $('#app');
const fileInput = $<HTMLInputElement>('#file-input');
const exportBtn = $<HTMLButtonElement>('#btn-export');
let anatomy: Anatomy | null = null;
let tree: Tree | null = null;

// ------------------------------------------------------------------ loading
async function openBytes(bytes: Uint8Array, name: string) {
  cleanup();
  landing.hidden = true;
  app.hidden = false;
  app.replaceChildren(h('div', { class: 'loading' }, `Reading ${name}…`));
  $('#filename').replaceChildren(h('span', { class: 'name' }, name), h('span', { class: 'badge' }, fmtBytes(bytes.length)));
  await new Promise((r) => setTimeout(r, 16)); // let the loading text paint

  const parser = detect(bytes);
  let pf: ParsedFile;
  let err: string | undefined;
  if (!parser) {
    pf = rawFile(bytes);
    err = 'This does not look like a supported audio file. It is not MP3, AAC, WAV, AIFF, FLAC, Ogg or MP4, as far as its first bytes show.';
  } else {
    try {
      const t = performance.now();
      pf = parser.parse(bytes);
      console.info(`parsed ${name} as ${parser.id} in ${(performance.now() - t).toFixed(1)} ms`, pf);
    } catch (e) {
      console.error(e);
      pf = rawFile(bytes);
      err = `The ${parser.id.toUpperCase()} parser failed: ${(e as Error).message}`;
    }
  }
  const m = new Model(pf, bytes, name, allExplanations, err);
  document.title = `${name} — audiofile.info`;
  $('#filename').append(h('span', { class: 'badge' }, pf.container), pf.audio.codec !== 'Unknown' && pf.audio.codec !== pf.container ? h('span', { class: 'badge' }, pf.audio.codec) : '');
  exportBtn.hidden = false;

  // Codec analysis: decision lanes, stored coefficients and per-frame detail.
  store.s.codec?.dispose();
  let codec: CodecCache | null = null;
  if (!err && m.frames.length) {
    const c = codecFor(pf, bytes);
    if (c) {
      m.codec = c;
      codec = new CodecCache(m, c);
    }
  }
  const D = m.duration || 1;
  store.set({ codec, lane: store.s.lane === 'stored' && !codec?.hasColumns ? 'spec' : store.s.lane, model: m, peaks: null, player: null, decode: parser && !err ? 'decoding' : 'error', decodeError: undefined, previewOnly: false, view: [0, D], bview: [0, 1], sel: null, hover: null });
  render(m);

  if (!parser || err) return;
  try {
    const d = await decode(bytes, m.duration, pf.audio.sampleRate);
    if (store.s.model !== m) return;
    const peaks = new Peaks(d.buffer);
    const url = d.previewOnly ? URL.createObjectURL(new Blob([bytes as BlobPart])) : null;
    const player = new Player(d.previewOnly ? null : d.buffer, url);
    // If the parser found no duration (rare), fall back to the decoded one.
    const patch: Parameters<typeof store.set>[0] = { peaks, player, decode: 'ok', previewOnly: d.previewOnly };
    if (!m.duration) patch.view = [0, d.buffer.duration];
    store.set(patch);
  } catch (e) {
    if (store.s.model !== m) return;
    // Raw PCM we can decode ourselves (AIFF, big-endian WAV, unusual bit depths).
    if (pf.pcm && pf.pcm.blockAlign > 0 && (pf.pcm.dataEnd - pf.pcm.dataStart) / pf.pcm.blockAlign * pf.pcm.channels < 400e6) {
      try {
        const d = decodePcm(bytes, pf.pcm, pf.format === 'wav');
        store.set({ peaks: new Peaks(d.buffer), player: new Player(d.buffer, null), decode: 'ok', previewOnly: false });
        return;
      } catch (e2) {
        console.warn('PCM fallback failed', e2);
      }
    }
    // The browser can't decode it; still allow playback attempts through <audio>.
    const url = URL.createObjectURL(new Blob([bytes as BlobPart]));
    const probe = new Audio();
    const can = probe.canPlayType(mimeFor(pf));
    store.set({ decode: 'error', decodeError: (e as Error).message || 'decode failed', player: can ? new Player(null, url) : null });
  }
}

function mimeFor(pf: ParsedFile) {
  return ({ mp3: 'audio/mpeg', mp2: 'audio/mpeg', aac: 'audio/aac', wav: 'audio/wav', aiff: 'audio/aiff', flac: 'audio/flac', ogg: 'audio/ogg', mp4: 'audio/mp4' } as const)[pf.format];
}

function cleanup() {
  store.s.player?.dispose();
  anatomy?.destroy();
  anatomy = null;
  tree = null;
}

function render(m: Model) {
  anatomy = new Anatomy(m);
  tree = new Tree(m);
  const insp = new Inspector(m);
  insp.setStepHandler((d) => step(d));
  const parts: HTMLElement[] = [];
  if (m.parseError) parts.push(h('section', { class: 'card error-card' }, h('h2', {}, 'Could not read this file fully'), h('p', {}, m.parseError), h('p', { class: 'dim' }, 'You can still look at the raw bytes below.')));
  parts.push(summaryCard(m), compositionCard(m), anatomy.el, h('div', { class: 'lower' }, tree.el, insp.el));
  if (store.s.codec) parts.push(encoderCard(store.s.codec));
  parts.push(bottomCards(m));
  app.replaceChildren(...parts);
  // Select the first informative block so the inspector is not empty.
  const first = m.top.find((s) => s.kind === 'header') ?? m.top[0];
  if (first) store.set({ sel: { kind: 'seg', seg: first } });
}

async function openFile(f: File) {
  const buf = new Uint8Array(await f.arrayBuffer());
  history.replaceState(null, '', location.pathname);
  await openBytes(buf, f.name);
}

async function openSample(id: string) {
  const s = SAMPLES.find((x) => x.id === id);
  if (!s) return;
  try {
    const r = await fetch(`/samples/${s.file}`);
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const buf = new Uint8Array(await r.arrayBuffer());
    history.replaceState(null, '', `#sample=${id}`);
    await openBytes(buf, s.file);
  } catch (e) {
    toast(`Could not load the sample: ${(e as Error).message}`);
  }
}

// ------------------------------------------------------------------ selection stepping
function step(dir: 1 | -1) {
  const s = store.s, m = s.model;
  if (!m || !tree) return;
  let seg = s.sel?.kind === 'seg' ? s.sel.seg : null;
  if (!seg) {
    // From a time selection or nothing: start at the frame under the playhead.
    const t = s.player?.time ?? 0;
    const i = m.frameIndexAtTime(t);
    seg = m.frames[Math.max(0, i)] ?? m.top[0];
  } else {
    seg = tree.siblingOf(seg, dir) ?? seg;
  }
  if (!seg) return;
  store.set({ sel: { kind: 'seg', seg } });
  reveal({ kind: 'seg', seg });
  if (s.audition && s.player && timeOf(m, seg)) playTarget({ kind: 'seg', seg });
}

function selectParent() {
  const s = store.s, m = s.model;
  if (!m || s.sel?.kind !== 'seg') return;
  const path = m.pathTo(s.sel.seg);
  if (path && path.length > 1) store.set({ sel: { kind: 'seg', seg: path[path.length - 2] } });
}

function selectFirstChild() {
  const s = store.s;
  if (s.sel?.kind !== 'seg') return;
  const c = kids(s.sel.seg)[0];
  if (c) {
    store.set({ sel: { kind: 'seg', seg: c } });
    reveal({ kind: 'seg', seg: c });
  }
}

// ------------------------------------------------------------------ events
fileInput.addEventListener('change', () => {
  const f = fileInput.files?.[0];
  if (f) void openFile(f);
  fileInput.value = '';
});
$('#btn-open').addEventListener('click', () => fileInput.click());
$('#dropzone').addEventListener('keydown', (e) => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    fileInput.click();
  }
});

let dragDepth = 0;
window.addEventListener('dragenter', (e) => {
  if (!e.dataTransfer?.types.includes('Files')) return;
  dragDepth++;
  document.body.classList.add('dragging');
});
window.addEventListener('dragleave', () => {
  dragDepth = Math.max(0, dragDepth - 1);
  if (!dragDepth) document.body.classList.remove('dragging');
});
window.addEventListener('dragover', (e) => e.preventDefault());
window.addEventListener('drop', (e) => {
  e.preventDefault();
  dragDepth = 0;
  document.body.classList.remove('dragging');
  const f = e.dataTransfer?.files?.[0];
  if (f) void openFile(f);
});

exportBtn.addEventListener('click', () => {
  const m = store.s.model;
  if (!m) return;
  const base = m.name.replace(/\.[^.]+$/, '');
  menu(exportBtn, [
    { label: 'Copy report as Markdown', sub: 'Summary, tags, structure and header fields', onClick: async () => { await navigator.clipboard.writeText(markdownReport(m)); toast('Markdown report copied'); } },
    { label: 'Download Markdown (.md)', onClick: () => download(markdownReport(m), `${base}.audiofile.md`, 'text/markdown') },
    { label: 'Download JSON (.json)', sub: 'Every block, field and frame, for programs', onClick: () => download(jsonReport(m), `${base}.audiofile.json`, 'application/json') },
  ]);
});

window.addEventListener('keydown', (e) => {
  if (!store.s.model) return;
  const tgt = e.target as HTMLElement;
  if (tgt.closest('input, textarea, select, [contenteditable]')) return;
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const k = e.key;
  const act: Record<string, () => void> = {
    ' ': () => togglePlay(),
    ArrowRight: () => step(1),
    ArrowLeft: () => step(-1),
    ArrowUp: () => selectParent(),
    ArrowDown: () => selectFirstChild(),
    '+': () => zoomTime(0.5),
    '=': () => zoomTime(0.5),
    '-': () => zoomTime(2),
    z: () => zoomToSelection(),
    f: () => fitAll(),
    s: () => {
      const order: ('wave' | 'spec' | 'stored')[] = store.s.codec?.hasColumns ? ['wave', 'spec', 'stored'] : ['wave', 'spec'];
      store.set({ lane: order[(order.indexOf(store.s.lane) + 1) % order.length] });
    },
    l: () => store.set({ loop: !store.s.loop }),
    a: () => { store.set({ audition: !store.s.audition }); toast(store.s.audition ? 'Play blocks while stepping: on' : 'Play blocks while stepping: off'); },
    Escape: () => store.set({ sel: null }),
  };
  const fn = act[k] ?? act[k.toLowerCase()];
  if (fn) {
    e.preventDefault();
    fn();
  }
});

// ------------------------------------------------------------------ landing
function landingArt() {
  const svg = $('.dz-art') as unknown as SVGSVGElement;
  const NS = 'http://www.w3.org/2000/svg';
  const mk = (tag: string, attrs: Record<string, string | number>) => {
    const e = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
    return e;
  };
  const W = 520, n = 26, meta = 70;
  const wave = svg.querySelector('.dz-wave')!, rib = svg.querySelector('.dz-ribbon')!, bytes = svg.querySelector('.dz-bytes')!;
  // A little waveform made of bars.
  for (let i = 0; i < 104; i++) {
    const x = 10 + i * 4.8;
    const env = Math.abs(Math.sin(i * 0.19) * 0.6 + Math.sin(i * 0.051) * 0.4);
    const hgt = 4 + env * 36 * (0.7 + 0.3 * Math.sin(i * 1.7));
    wave.append(mk('rect', { x, y: 30 - hgt / 2, width: 2.6, height: hgt, rx: 1.3, fill: 'var(--k-audio)', opacity: 0.85 }));
  }
  // Bytes: tags + header, then frames of varying size; ribbons join frames to equal slices of time.
  bytes.append(mk('rect', { x: 10, y: 122, width: meta - 14, height: 18, rx: 3, fill: 'var(--k-metadata)' }));
  bytes.append(mk('rect', { x: meta - 2, y: 122, width: 10, height: 18, rx: 2, fill: 'var(--k-header)' }));
  const sizes = Array.from({ length: n }, (_, i) => 0.6 + 0.8 * Math.abs(Math.sin(i * 0.9)));
  const tot = sizes.reduce((a, b) => a + b, 0);
  let bx = meta + 10;
  const bw = W - 10 - bx;
  for (let i = 0; i < n; i++) {
    const w = (sizes[i] / tot) * bw;
    const tx = 10 + (i / n) * (W - 20), tw = (W - 20) / n;
    rib.append(mk('path', {
      d: `M${tx},56 C${tx},90 ${bx},90 ${bx},122 L${bx + w - 1},122 C${bx + w - 1},90 ${tx + tw - 1},90 ${tx + tw - 1},56 Z`,
      fill: 'var(--k-audio)', opacity: i % 2 ? 0.16 : 0.26,
    }));
    bytes.append(mk('rect', { x: bx, y: 122, width: w - 1.2, height: 18, rx: 2, fill: 'var(--k-audio)', opacity: i % 2 ? 0.75 : 1 }));
    bx += w;
  }
}

function initLanding() {
  const list = $('#sample-list');
  for (const s of SAMPLES) {
    const b = h('button', { class: 'chip', title: s.sub }, h('span', { class: 'dot' }), s.label);
    b.onclick = () => void openSample(s.id);
    list.append(b);
  }
  landingArt();
  const m = location.hash.match(/sample=(\w+)/);
  if (m) void openSample(m[1]);
}
initLanding();

if (import.meta.env.DEV) (window as unknown as { store: typeof store }).store = store;
